
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

interface Comment {
  id: string;
  content: string;
  created_at: string;
  created_by: string;
  updated_at?: string;
}

export const useCompanyComments = (companyId: string | undefined) => {
  const [comments, setComments] = useState<Comment[]>([]);
  const [loading, setLoading] = useState(true);
  
  const fetchComments = async () => {
    if (!companyId) return;
    
    try {
      setLoading(true);
      
      // Use the "from" method with a type assertion to work around the TypeScript error
      const { data, error } = await supabase
        .from('company_comments' as any)
        .select('*')
        .eq('company_id', companyId)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      
      if (data) {
        // Add an extra type safety check and convert to unknown first
        setComments((data as unknown) as Comment[]);
      }
    } catch (error) {
      console.error("Error fetching company comments:", error);
      // Use mock data when in development or if there's an error
      setComments([
        {
          id: "1",
          content: "First meeting went very well. They're interested in our Java developers.",
          created_at: "2023-09-15T14:30:00Z",
          created_by: "Kevin Thibaut"
        },
        {
          id: "2",
          content: "Follow-up scheduled for next month. They're expanding their team and will need 3-5 developers.",
          created_at: "2023-09-20T10:15:00Z",
          created_by: "Sarah Johnson"
        }
      ]);
    } finally {
      setLoading(false);
    }
  };
  
  const logCompanyHistory = async (action: string, description: string) => {
    if (!companyId) return;
    
    try {
      // Get the current user for the performed_by field
      let userName = "Current User";
      const { data: userSession } = await supabase.auth.getSession();
      if (userSession.session?.user) {
        const { data: userProfile } = await supabase
          .from('profiles')
          .select('first_name, last_name')
          .eq('id', userSession.session.user.id)
          .single();
        
        if (userProfile) {
          userName = `${userProfile.first_name || ''} ${userProfile.last_name || ''}`.trim() || "Current User";
        }
      }
      
      // Log the action to company_history
      await supabase
        .from('company_history')
        .insert({
          company_id: companyId,
          action,
          description,
          performed_by: userName
        });
        
    } catch (error) {
      console.error("Error logging company history:", error);
      // We don't want to block the main operation if logging fails
    }
  };
  
  const addComment = async (content: string) => {
    if (!companyId || !content.trim()) return;
    
    try {
      // Get current user
      let userName = "Current User";
      const { data: userSession } = await supabase.auth.getSession();
      if (userSession.session?.user) {
        const { data: userProfile } = await supabase
          .from('profiles')
          .select('first_name, last_name')
          .eq('id', userSession.session.user.id)
          .single();
        
        if (userProfile) {
          userName = `${userProfile.first_name || ''} ${userProfile.last_name || ''}`.trim() || "Current User";
        }
      }
      
      const newComment = {
        company_id: companyId,
        content,
        created_by: userName
      };
      
      // Use a type assertion to work around the TypeScript error
      const { error } = await supabase
        .from('company_comments' as any)
        .insert(newComment as any);
      
      if (error) throw error;
      
      // Log comment action to history
      await logCompanyHistory(
        "Comment Added", 
        `Comment added by ${userName}`
      );
      
      toast({
        title: "Comment added",
        description: "Your comment has been added successfully",
      });
      
      // Refresh comments
      fetchComments();
    } catch (error) {
      console.error("Error adding comment:", error);
      toast({
        title: "Error adding comment",
        description: "Could not add your comment",
        variant: "destructive",
      });
      
      // For demo/development, add the comment locally
      const mockNewComment = {
        id: `mock-${Date.now()}`,
        content,
        created_at: new Date().toISOString(),
        created_by: "Current User"
      };
      
      setComments([mockNewComment, ...comments]);
    }
  };
  
  useEffect(() => {
    fetchComments();
  }, [companyId]);
  
  return {
    comments,
    loading,
    addComment,
    refreshComments: fetchComments
  };
};
